import { isTripCancelledByAdmin, isTripRefundedByCustomer } from '@/root/shared-helpers/reservation/sail-cancellation'
import type { Reservation, TicketData } from '@/root/shared-types/reservation/reservation-types'

export type TicketPageView =
  | { kind: 'notFound' }
  | { kind: 'paymentPending' }
  | { kind: 'success' }
  | { kind: 'booked' }
  | { kind: 'cancelledByAdmin' }
  | { kind: 'bookedCancelledByAdmin' }
  | { kind: 'editExpired'; days?: number }
  | { kind: 'cancelledByCustomer' }

interface TicketActions {
  reservationId: number
  reservationToken: string
  canEdit: boolean
  canCancel: boolean
}

interface TicketPageState {
  view: TicketPageView
  /** Download, print, edit and cancel; null when the page offers none of them */
  actions: TicketActions | null
  hideQrCode: boolean
}

const getIssuedTicketView = ({ status, isRoundTrip, there, back }: TicketData): TicketPageView => {
  const isBooked = status === 'BOOKED'
  const expiredThere = Boolean(there.cancellationInfo?.expired)
  const expiredBack = Boolean(back?.cancellationInfo?.expired)
  const isCancelledByAdmin = isTripCancelledByAdmin(there.isCancelled, back?.isCancelled)

  // The first matching case wins, so the order of the cases is the priority of the views
  switch (true) {
    case isTripRefundedByCustomer(there.isRefund, back?.isRefund, isRoundTrip):
      return { kind: 'cancelledByCustomer' }
    case isBooked && isCancelledByAdmin:
      return { kind: 'bookedCancelledByAdmin' }
    case isCancelledByAdmin && !expiredThere && !expiredBack:
      return { kind: 'cancelledByAdmin' }
    case expiredThere:
      return { kind: 'editExpired', days: there.cancellationInfo?.expireCancelledSailsInXDays }
    case expiredBack:
      return { kind: 'editExpired', days: back.cancellationInfo?.expireCancelledSailsInXDays }
    case isBooked:
      return { kind: 'booked' }
    default:
      return { kind: 'success' }
  }
}

const getView = (ticketData: TicketData): TicketPageView | null => {
  switch (ticketData.status) {
    case 'DL':
      return { kind: 'notFound' }
    case 'OFFER':
      return { kind: 'paymentPending' }
    case 'BOOKED':
    case 'OK':
    case 'CN':
      return getIssuedTicketView(ticketData)
    default:
      return null
  }
}

/** Which state the ticket page shows and which actions it offers; null means nothing to render (TEMP). */
export const getTicketPageView = (
  ticketData: TicketData,
  allowance: Reservation['allowance']
): TicketPageState | null => {
  const view = getView(ticketData)

  if (!view) return null

  // A BOOKED reservation is not paid yet, so it can be neither edited nor cancelled; an expired one keeps the allowance
  const isActionLocked = view.kind === 'booked' || view.kind === 'bookedCancelledByAdmin'

  const { reservationId, token } = ticketData

  return {
    view,
    actions:
      view.kind !== 'cancelledByCustomer' && reservationId && token
        ? {
            reservationId,
            reservationToken: token,
            canEdit: Boolean(allowance?.edit) && !isActionLocked,
            canCancel: Boolean(allowance?.cancel) && !isActionLocked,
          }
        : null,
    hideQrCode: ticketData.status === 'BOOKED',
  }
}
